<script setup lang="ts">
import CalendarControlButton from '@ui/components/calendar/picker/CalendarControlButton.vue'
import CalendarHeaderWeekdays from '@ui/components/calendar/picker/day/CalendarHeaderWeekdays.vue'
import { CalendarMode } from '@ui/components/calendar/picker/index'
import CalendarHeaderDays from '@ui/components/calendar/picker/time/CalendarHeaderDays.vue'
import { getCalendarWeek } from '@ui/components/calendar/picker/time/calendarWeek'
import { getMonthName } from '@ui/utils/times'
import { addMonths, addWeeks, addYears, endOfMonth, endOfYear, format, startOfMonth, startOfYear } from 'date-fns'
import ArrowLeft from '~icons/cue-regular/arrow-left'
import ArrowRight from '~icons/cue-regular/arrow-right'
import { isCalendarIntervalDisabled } from './calendarAvailability'

const props = withDefaults(
  defineProps<{
    today?: Date
    disabledDate?: (date: Date) => boolean
    disabled?: boolean
    highlightSelected?: boolean
    returnMode?: CalendarMode
  }>(),
  {
    today: () => new Date(),
    highlightSelected: true,
    returnMode: CalendarMode.Days,
  },
)

const emit = defineEmits<{ 'select:day': [day: Date] }>()
const modelValue = defineModel<Date>({ default: () => new Date() })
const mode = defineModel<CalendarMode>('mode', { default: CalendarMode.Days })
const previousWeekDate = computed(() => {
  const day = addWeeks(modelValue.value, -1)
  return props.disabledDate?.(day) ? getCalendarWeek(day).find(date => !props.disabledDate?.(date)) : day
})
const previousDisabled = computed(() => {
  if (props.disabled) {
    return true
  }
  if (!props.disabledDate) {
    return false
  }
  switch (mode.value) {
    case CalendarMode.Times:
      return !previousWeekDate.value
    case CalendarMode.Days: {
      const month = addMonths(modelValue.value, -1)
      return isCalendarIntervalDisabled({ start: startOfMonth(month), end: endOfMonth(month) }, props.disabledDate)
    }
    case CalendarMode.Months: {
      const year = addYears(modelValue.value, -1)
      return isCalendarIntervalDisabled({ start: startOfYear(year), end: endOfYear(year) }, props.disabledDate)
    }
    case CalendarMode.Years:
      return isCalendarIntervalDisabled(
        { start: startOfYear(addYears(modelValue.value, -12)), end: endOfYear(addYears(modelValue.value, -1)) },
        props.disabledDate,
      )
  }
  return false
})

function onNavigate(inc: number) {
  if (props.disabled || (inc < 0 && previousDisabled.value)) {
    return
  }
  switch (mode.value) {
    case CalendarMode.Years:
      modelValue.value = addYears(modelValue.value, inc * 12)
      break
    case CalendarMode.Months:
      modelValue.value = addYears(modelValue.value, inc)
      break
    case CalendarMode.Days:
      modelValue.value = addMonths(modelValue.value, inc)
      break
    case CalendarMode.Times: {
      const day = inc < 0 ? previousWeekDate.value : addWeeks(modelValue.value, inc)
      if (day) {
        modelValue.value = day
      }
      break
    }
  }
}

function setMode(m: CalendarMode) {
  if (props.disabled) {
    return
  }
  mode.value = mode.value === m ? props.returnMode : m
}
</script>

<template lang="pug">
.header.flex.col-12(:class="mode === CalendarMode.Days || mode === CalendarMode.Times ? 'p-b-8' : 'p-b-12'")
  .calendar-navigation.flex-between-center.p-2-12.h-36(data-id="calendar-navigation" :data-month="format(modelValue, 'yyyy-MM')")
    CalendarControlButton(data-id="calendar-previous" :icon="ArrowLeft" :disabled="previousDisabled" @click="onNavigate(-1)")
    .flex-center-center
      CalendarControlButton(v-if="returnMode !== CalendarMode.Months" :active="mode === CalendarMode.Months" :disabled="disabled" @click="setMode(CalendarMode.Months)") {{ getMonthName(modelValue) }}
      CalendarControlButton(:active="mode === CalendarMode.Years" :disabled="disabled" @click="setMode(CalendarMode.Years)") {{ modelValue.getFullYear() }}
    CalendarControlButton(data-id="calendar-next" :icon="ArrowRight" :disabled="disabled" @click="onNavigate(1)")
  Transition
    CalendarHeaderWeekdays(v-if="mode === CalendarMode.Days" :today="today")
    CalendarHeaderDays(v-else-if="mode === CalendarMode.Times" v-model="modelValue" :today="today" :disabledDate="disabledDate" :disabled="disabled" :highlightSelected="highlightSelected" @select="emit('select:day', $event)")
</template>

<style scoped>
.header {
  border-bottom: 1px solid var(--cue-border-neutral-secondary);
}
</style>
